import React, { useMemo } from 'react';
import Animated from 'animated/lib/targets/react-dom';
import styled, { css } from 'styled-components';
import { useReactTable, getCoreRowModel, flexRender, createColumnHelper } from '@tanstack/react-table';
import useGetBatllesTooltipInfo from 'shared/screens/Battles/hooks/useGetBatllesTooltipInfo';
import { BATTLES_STANDING_TYPE } from 'shared/screens/Battles/constants/dictionary';
import { getText, TEXT_KEY } from 'shared/utils/localization';
import ScalableText from '../ScalableText/ScalableText';

const columnHelper = createColumnHelper();

function BattleProgressTooltip({ props }) {
  const { animationClassName } = props;
  const { battlesAssets, currentGame } = useGetBatllesTooltipInfo();
  const tooltipHeader = currentGame?.tooltipHeader;

  const columns = useMemo(
    () => [
      columnHelper.accessor('rank', {
        header: getText(TEXT_KEY.BATTLES_RANK),
        cell: info => info.getValue(),
      }),
      columnHelper.accessor('name', {
        header: getText(TEXT_KEY.BATTLES_PLAYER),
        cell: info => info.getValue(),
      }),
      columnHelper.accessor('progress', {
        header: getText(TEXT_KEY.BATTLES_PROGRESS),
        cell: info => `${info.getValue()}%`,
      }),
    ],
    []
  );

  const table = useReactTable({
    data: currentGame?.standings || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <>
      {tooltipHeader && (
        <Animated.div
          className={`Mission-Tooltip-Container ${animationClassName ?? 'AnimationFadeIn'}`}
          style={{ ...props.style }}
        >
          <TooltipContainer backgroundUrl={battlesAssets?.BATTLES_TOP_BAR_LEADERBOARD_BACKING}>
            <ScalableText wrapperStyle={TitleWrapperStyle} textStyle={TitleStyle} text={tooltipHeader} />
            <LeaderboardHeader>
              {table.getHeaderGroups().map(headerGroup => (
                <React.Fragment key={headerGroup.id}>
                  {headerGroup.headers.map((header, index) => (
                    <TableHeaderItem index={index} key={header.id}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHeaderItem>
                  ))}
                </React.Fragment>
              ))}
            </LeaderboardHeader>

            {table.getRowModel().rows.map((row, rowIndex) => (
              <Row key={row.id} highlight={row.original.type === BATTLES_STANDING_TYPE.PLAYER}>
                {row.getVisibleCells().map((cell, index) => (
                  <TableCellItemWrapper
                    key={cell.id}
                    index={index}
                    columnId={cell.column.id}
                    highlight={row.original.type === BATTLES_STANDING_TYPE.PLAYER}
                  >
                    <TableCellItem index={index}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCellItem>
                  </TableCellItemWrapper>
                ))}
              </Row>
            ))}
          </TooltipContainer>
        </Animated.div>
      )}
    </>
  );
}

export default BattleProgressTooltip;

const TooltipContainer = styled.div`
  width: 480px;
  height: 295px;
  padding: 12px;
  background-image: url(${props => props.backgroundUrl});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  color: white;
  border-radius: 10px;
`;

const TitleStyle = css`
  font-weight: bold;
  font-size: 1.7rem;
  text-align: center;
  color: #fff;
  text-transform: uppercase;
  max-width: 100%;

  ${({ theme }) => theme.BattleProgressTooltip.Title}
`;

const TitleWrapperStyle = css`
  margin: 3px 0 10px 0;
  position: relative;
  width: 100%;
  height: 2.8rem;
  overflow: visible;
`;

const LeaderboardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  padding: 4px 8px;
  ${({ theme }) => theme.BattleProgressTooltip.LeaderboardHeader}
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 8px;
  margin-top: 3px;
  background-color: ${({ highlight }) => (highlight ? '#f9b233' : 'transparent')};
  border-radius: ${({ highlight }) => (highlight ? '20px' : '6px')};
  font-weight: ${({ highlight }) => (highlight ? 'bold' : 'normal')};
  color: ${({ highlight }) => (highlight ? '#000' : '#fff')};
`;

const TableHeaderItem = styled.div`
  flex: ${({ index }) => (index === 1 ? 1.5 : 1)};
  display: flex;
  justify-content: center;
  font-weight: bold;
  font-size: 18px;
  text-transform: uppercase;
`;

const TableCellItemWrapper = styled.div`
  flex: ${({ index }) => (index === 1 ? 1.5 : 1)};
  display: flex;
  justify-content: ${({ index }) => (index === 1 ? 'flex-start' : 'center')};
  color: ${({ highlight }) => (highlight ? '#470702' : '#fff')};
  font-size: 18px;
`;

const TableCellItem = styled.span`
  margin-left: ${({ index }) => (index === 1 ? '20%' : 0)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

